import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Plus, Image as ImageIcon, Upload, X, MapPin, Settings2, Star, GripVertical } from "lucide-react";

export default function ProjectsPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [galleryFor, setGalleryFor] = useState(null);
  const [settingsFor, setSettingsFor] = useState(null);
  const [form, setForm] = useState({ name: "", location: "", description: "", total_plots: 0 });

  const load = useCallback(() => api.get("/projects").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/projects", { ...form, total_plots: Number(form.total_plots) });
      toast.success("Project created");
      setOpen(false); setForm({ name: "", location: "", description: "", total_plots: 0 });
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="heading text-3xl md:text-4xl">Projects</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-project-btn"><Plus size={14} className="mr-2" />New project</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New project</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name-input" /></div>
              <div><Label>Location</Label><Input required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="project-location-input" /></div>
              <div><Label>Total plots</Label><Input type="number" value={form.total_plots} onChange={(e) => setForm({ ...form, total_plots: e.target.value })} data-testid="project-total-input" /></div>
              <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <DialogFooter><Button type="submit" data-testid="project-save-btn">Create</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="border border-border rounded-lg bg-card overflow-hidden flex flex-col" data-testid={`project-${p.id}`}>
            {p.cover_image && <img src={p.cover_image} alt="" className="w-full max-h-[160px] object-cover" />}
            <div className="p-4 flex-1 flex flex-col">
              <div className="heading text-lg">{p.name}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={11} /> {p.location}</div>
              <div className="text-xs text-muted-foreground mt-2">{p.total_plots} plots · {p.gallery?.length || 0} photos</div>
              {p.manager_email && <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1 truncate">Manager · {p.manager_email}</div>}
              {p.sister_discount_percent != null && <div className="text-[10px] uppercase tracking-widest text-primary mt-1 font-semibold">Sister discount · {p.sister_discount_percent}%</div>}
              <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setGalleryFor(p)} data-testid={`manage-gallery-${p.id}`}>
                  <ImageIcon size={14} className="mr-2" /> Photos
                </Button>
                <Button size="sm" variant="outline" onClick={() => setSettingsFor(p)} data-testid={`manage-settings-${p.id}`}>
                  <Settings2 size={14} className="mr-2" /> Settings
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <GalleryDialog project={galleryFor} onClose={() => { setGalleryFor(null); load(); }} />
      <ProjectSettingsDialog project={settingsFor} onClose={() => { setSettingsFor(null); load(); }} />
    </div>
  );
}

function ProjectSettingsDialog({ project, onClose }) {
  const [form, setForm] = useState({ manager_email: "", sister_discount_percent: "", default_nightly_rate: "", accent_color: "", cover_image: "", receipt_logo_url: "", manager_phone: "", wifi_ssid: "", wifi_password: "", directions: "" });
  const [busyLogo, setBusyLogo] = useState(false);
  const logoRef = useRef(null);
  useEffect(() => {
    if (project) {
      setForm({
        manager_email: project.manager_email || "",
        sister_discount_percent: project.sister_discount_percent ?? "",
        default_nightly_rate: project.default_nightly_rate ?? "",
        accent_color: project.accent_color || "",
        cover_image: project.cover_image || "",
        receipt_logo_url: project.receipt_logo_url || "",
        manager_phone: project.manager_phone || "",
        wifi_ssid: project.wifi_ssid || "",
        wifi_password: project.wifi_password || "",
        directions: project.directions || "",
      });
    }
  }, [project]);
  if (!project) return null;
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB"); return; }
    setBusyLogo(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${backend}/api/gallery-file/${data.storage_path}`;
      setForm((prev) => ({ ...prev, receipt_logo_url: url }));
      toast.success("Logo uploaded — remember to save");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusyLogo(false); if (logoRef.current) logoRef.current.value = ""; }
  };
  const save = async (e) => {
    e.preventDefault();
    try {
      const patch = {
        manager_email: form.manager_email.trim() || null,
        sister_discount_percent: form.sister_discount_percent === "" ? null : Number(form.sister_discount_percent),
        default_nightly_rate: form.default_nightly_rate === "" ? null : Number(form.default_nightly_rate),
        accent_color: form.accent_color.trim() || null,
        cover_image: form.cover_image.trim() || null,
        receipt_logo_url: form.receipt_logo_url.trim() || null,
        manager_phone: form.manager_phone.trim() || null,
        wifi_ssid: form.wifi_ssid.trim() || null,
        wifi_password: form.wifi_password.trim() || null,
        directions: form.directions.trim() || null,
      };
      await api.patch(`/projects/${project.id}`, patch);
      toast.success("Project updated");
      onClose();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!project} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Settings · {project?.name}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div>
            <Label>Manager email</Label>
            <Input type="email" placeholder="manager@example.com" value={form.manager_email}
                   onChange={(e) => setForm({ ...form, manager_email: e.target.value })}
                   data-testid="project-manager-email-input" />
            <p className="text-[11px] text-muted-foreground mt-1">Enquiries for this project are routed to this inbox (falls back to the global manager).</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Sister discount (%)</Label>
              <Input type="number" step="1" min="0" max="100" placeholder="15" value={form.sister_discount_percent}
                     onChange={(e) => setForm({ ...form, sister_discount_percent: e.target.value })}
                     data-testid="project-discount-input" />
            </div>
            <div>
              <Label>Default nightly rate (₹)</Label>
              <Input type="number" step="50" min="0" placeholder="5000" value={form.default_nightly_rate}
                     onChange={(e) => setForm({ ...form, default_nightly_rate: e.target.value })}
                     data-testid="project-rate-input" />
            </div>
          </div>
          <div>
            <Label>Accent colour (hex)</Label>
            <div className="flex gap-2 items-center">
              <Input type="text" placeholder="#3A7050" value={form.accent_color}
                     onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                     data-testid="project-accent-input" />
              <div className="h-9 w-9 rounded-md border border-border" style={{ background: form.accent_color || "transparent" }} />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Used as a chip on the project card and in the sidebar badge.</p>
          </div>
          <div>
            <Label>Cover image URL</Label>
            <Input type="url" placeholder="https://…" value={form.cover_image}
                   onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                   data-testid="project-cover-input" />
          </div>
          <div>
            <Label>Receipt logo</Label>
            <div className="flex gap-2 items-center">
              {form.receipt_logo_url && (
                <img src={form.receipt_logo_url} alt="logo" className="h-10 w-10 rounded-md object-contain border border-border bg-background" data-testid="project-logo-preview" />
              )}
              <input type="file" accept="image/*" ref={logoRef} onChange={uploadLogo} className="hidden" data-testid="project-logo-file" />
              <Button type="button" size="sm" variant="outline" onClick={() => logoRef.current?.click()} disabled={busyLogo} data-testid="project-logo-upload-btn">
                <Upload size={12} className="mr-1" />{busyLogo ? "Uploading…" : (form.receipt_logo_url ? "Replace logo" : "Upload logo")}
              </Button>
              {form.receipt_logo_url && (
                <Button type="button" size="sm" variant="ghost" onClick={() => setForm({ ...form, receipt_logo_url: "" })} data-testid="project-logo-clear-btn">
                  <X size={12} className="mr-1" /> Remove
                </Button>
              )}
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${project.id}/receipt-preview`}
                 target="_blank" rel="noreferrer"
                 className="ml-auto text-xs inline-flex items-center gap-1 text-primary hover:underline"
                 data-testid="project-receipt-preview-link">
                Preview receipt →
              </a>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Shown on the payment-receipt PDF next to the accent band. Square logos work best (≤2 MB). Save first to preview the latest changes.</p>
          </div>
          <div className="border-t border-border pt-3 mt-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-2">Guest portal</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Manager phone</Label><Input placeholder="+91 …" value={form.manager_phone} onChange={(e) => setForm({ ...form, manager_phone: e.target.value })} data-testid="project-phone-input" /></div>
              <div><Label>WiFi SSID</Label><Input value={form.wifi_ssid} onChange={(e) => setForm({ ...form, wifi_ssid: e.target.value })} data-testid="project-wifi-ssid-input" /></div>
              <div className="col-span-2"><Label>WiFi password</Label><Input value={form.wifi_password} onChange={(e) => setForm({ ...form, wifi_password: e.target.value })} data-testid="project-wifi-pass-input" /></div>
              <div className="col-span-2"><Label>Directions</Label><Textarea rows={3} value={form.directions} onChange={(e) => setForm({ ...form, directions: e.target.value })} placeholder="Turn left at the mango tree…" data-testid="project-directions-input" /></div>
            </div>
          </div>
          <DialogFooter><Button type="submit" data-testid="project-settings-save-btn">Save</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GalleryDialog({ project, onClose }) {
  const [gallery, setGallery] = useState([]);
  const [coverImage, setCoverImage] = useState("");
  const [busy, setBusy] = useState(false);
  const [replacingIdx, setReplacingIdx] = useState(null);
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const fileRef = useRef(null);
  const replaceRef = useRef(null);

  useEffect(() => {
    setGallery(project?.gallery || []);
    setCoverImage(project?.cover_image || "");
  }, [project]);

  if (!project) return null;

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBusy(true);
    try {
      const newUrls = [];
      const backend = process.env.REACT_APP_BACKEND_URL;
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: too large`); continue; }
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newUrls.push(`${backend}/api/gallery-file/${data.storage_path}`);
      }
      const next = [...gallery, ...newUrls];
      await api.patch(`/projects/${project.id}`, { gallery: next });
      setGallery(next);
      toast.success(`Added ${newUrls.length} photo(s)`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const startReplace = (idx) => {
    setReplacingIdx(idx);
    replaceRef.current?.click();
  };

  const doReplace = async (e) => {
    const file = e.target.files?.[0];
    const idx = replacingIdx;
    if (!file || idx == null) { setReplacingIdx(null); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Too large (10MB max)"); setReplacingIdx(null); return; }
    setBusy(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newUrl = `${backend}/api/gallery-file/${data.storage_path}`;
      const next = gallery.map((u, i) => (i === idx ? newUrl : u));
      await api.patch(`/projects/${project.id}`, { gallery: next });
      setGallery(next);
      toast.success("Photo replaced");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false); setReplacingIdx(null);
      if (replaceRef.current) replaceRef.current.value = "";
    }
  };

  const addUrl = async () => {
    const url = prompt("Paste image URL");
    if (!url) return;
    const next = [...gallery, url];
    try {
      await api.patch(`/projects/${project.id}`, { gallery: next });
      setGallery(next);
      toast.success("Added");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const remove = async (idx) => {
    const next = gallery.filter((_, i) => i !== idx);
    try {
      await api.patch(`/projects/${project.id}`, { gallery: next });
      setGallery(next);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const setAsCover = async (url) => {
    try {
      await api.patch(`/projects/${project.id}`, { cover_image: url });
      setCoverImage(url);
      toast.success("Cover updated");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const reorder = async (from, to) => {
    if (from == null || to == null || from === to) return;
    const next = [...gallery];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    const prev = gallery;
    setGallery(next);
    try {
      await api.patch(`/projects/${project.id}`, { gallery: next });
      toast.success("Photo order updated");
    } catch (err) {
      setGallery(prev);
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={!!project} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Gallery · {project?.name}</DialogTitle>
          <DialogDescription>Drag any tile by its handle to reorder. Hover for Set-as-cover, Change and Remove actions.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          {gallery.map((url, i) => {
            const isCover = url === coverImage;
            const isDragging = draggedIdx === i;
            const isDropTarget = dragOverIdx === i && draggedIdx !== null && draggedIdx !== i;
            return (
              <div key={`${i}-${url}`}
                   className={`relative group border rounded-md overflow-hidden aspect-video transition-all ${
                     isDragging ? "opacity-40 border-primary" :
                     isDropTarget ? "border-primary border-2 ring-2 ring-primary/30" :
                     "border-border"
                   }`}
                   draggable
                   onDragStart={(e) => { setDraggedIdx(i); e.dataTransfer.effectAllowed = "move"; }}
                   onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dragOverIdx !== i) setDragOverIdx(i); }}
                   onDragLeave={() => { if (dragOverIdx === i) setDragOverIdx(null); }}
                   onDrop={(e) => { e.preventDefault(); reorder(draggedIdx, i); setDraggedIdx(null); setDragOverIdx(null); }}
                   onDragEnd={() => { setDraggedIdx(null); setDragOverIdx(null); }}
                   data-testid={`gallery-tile-${i}`}>
                <img src={url} alt="" className="w-full h-full object-cover pointer-events-none" />
                <span className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/90 border border-border inline-flex items-center justify-center cursor-grab active:cursor-grabbing shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Drag to reorder"
                      aria-label="Drag to reorder"
                      data-testid={`gallery-drag-${i}`}>
                  <GripVertical size={12} className="text-muted-foreground" />
                </span>
                {isCover && (
                  <span className="absolute top-1 left-1 text-[9px] uppercase tracking-widest rounded-full px-2 py-0.5 bg-primary text-primary-foreground font-semibold inline-flex items-center gap-1 shadow" data-testid={`gallery-cover-badge-${i}`}>
                    <Star size={9} fill="currentColor" /> Cover
                  </span>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5 pointer-events-none">
                  <div className="flex gap-1.5 pointer-events-auto">
                  {!isCover && (
                    <button type="button" onClick={() => setAsCover(url)} disabled={busy}
                            title="Set as cover"
                            aria-label="Set as cover"
                            className="h-8 w-8 rounded-full bg-background/95 border border-border font-semibold hover:bg-primary hover:text-primary-foreground inline-flex items-center justify-center"
                            data-testid={`gallery-setcover-${i}`}>
                      <Star size={13} />
                    </button>
                  )}
                  <button type="button" onClick={() => startReplace(i)} disabled={busy}
                          title="Change photo"
                          aria-label="Change photo"
                          className="h-8 w-8 rounded-full bg-background/95 border border-border font-semibold hover:bg-background inline-flex items-center justify-center"
                          data-testid={`gallery-replace-${i}`}>
                    <Upload size={13} />
                  </button>
                  <button type="button" onClick={() => remove(i)}
                          title="Remove photo"
                          aria-label="Remove photo"
                          className="h-8 w-8 rounded-full bg-background/95 border border-border font-semibold hover:bg-red-100 hover:text-red-800 inline-flex items-center justify-center"
                          data-testid={`gallery-remove-${i}`}>
                    <X size={13} />
                  </button>
                  </div>
                </div>
              </div>
            );
          })}
          {gallery.length === 0 && <div className="col-span-3 text-sm text-muted-foreground text-center py-6 border border-dashed border-border rounded-md">No photos yet.</div>}
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={upload} data-testid="gallery-file-input" />
        <input ref={replaceRef} type="file" accept="image/*" hidden onChange={doReplace} data-testid="gallery-replace-input" />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={addUrl} data-testid="gallery-add-url-btn">Add URL</Button>
          <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="gallery-upload-btn">
            <Upload size={14} className="mr-2" />{busy ? "Uploading…" : "Upload photos"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
